import { CareTeamExpertDetail, CareTeamExpertResponse } from "#/components/care-team/hooks/useCareTeamExperts"
import { ExpertBio } from "#/components/read/shared/ExpertBioMapper"
import { TileDisplay } from "#/components/talk-to-carrot/util/utils"

export type CareTeamExpertServiceWithTile = CareTeamExpertDetail & {
  tileEntry?: TileDisplay
}

export type CareTeamExpertWithTiles = {
  expertBio: ExpertBio
  services: CareTeamExpertServiceWithTile[]
}

/*
  Because we are using two systems to keep track of the expert bios and their chats, there are various edge cases, some of which are quite complex.
  
  Follow this Figjam: https://www.figma.com/board/kIb40tRJjM6kJ69spdOYhY/Care-team-eng?node-id=397-743&p=f&t=CGOGjqNGFzQBIckm-0 to follow and run through the various edge cases
*/

/**
 * Consolidates expert bios and their services based on professionalId and reasonIds.
 * @param expertBios Array of expert bios.
 * @param careTeamExperts Array of care team experts.
 * @returns Array of CareTeamExpertWithTiles with consolidated data.
 */

// Helper: Get matching services for a bio
function getMatchingServicesForBio(
  bio: ExpertBio,
  servicesMap: Map<number, CareTeamExpertDetail>
): CareTeamExpertServiceWithTile[] {
  if (!servicesMap) return []
  if (bio.timeTapReasons && bio.timeTapReasons.length > 0) {
    // Only include services that match the bio's reasonIds
    return bio.timeTapReasons
      .map((reason) => servicesMap.get(reason.reasonId))
      .filter((service): service is CareTeamExpertDetail => Boolean(service))
      .map((service): CareTeamExpertServiceWithTile => ({ ...service, tileEntry: undefined }))
  } else {
    // If no reasonIds, include all services
    return Array.from(servicesMap.values()).map(
      (service): CareTeamExpertServiceWithTile => ({
        ...service,
        tileEntry: undefined
      })
    )
  }
}

export const consolidateExpertBios = (
  expertBios: ExpertBio[],
  careTeamExperts: CareTeamExpertResponse[]
): CareTeamExpertWithTiles[] => {
  // Map experts by professionalId for quick lookup
  const expertMap = new Map(careTeamExperts.map((expert) => [expert.timeTapStaffId, expert]))

  // Pre-map each expert's services by reasonId for O(1) service lookup
  const professionalToServicesMap = new Map<number, Map<number, CareTeamExpertDetail>>()
  careTeamExperts.forEach((expert) => {
    const servicesMap = new Map<number, CareTeamExpertDetail>()
    expert.services?.forEach((service) => {
      servicesMap.set(service.reasonId, service)
    })
    professionalToServicesMap.set(expert.timeTapStaffId, servicesMap)
  })

  // Group bios by professionalId
  const biosByProfessionalId = expertBios.reduce((map, bio) => {
    const existingBios = map.get(bio.professionalId) || []
    map.set(bio.professionalId, [...existingBios, bio])
    return map
  }, new Map<number, ExpertBio[]>())

  // Main logic: consolidate bios and services
  return Array.from(biosByProfessionalId.entries())
    .filter(([professionalId]) => expertMap.has(professionalId))
    .map(([professionalId, bios]) => {
      const servicesMap = professionalToServicesMap.get(professionalId)

      // Collect all matching services from all bios
      const allMatchingServices: CareTeamExpertServiceWithTile[] = []
      let selectedBio: ExpertBio | undefined

      for (const bio of bios) {
        const matches = getMatchingServicesForBio(bio, servicesMap)
        if (matches.length > 0) {
          if (!selectedBio) selectedBio = bio // Pick the first bio with a match
          allMatchingServices.push(...matches)
        }
      }

      // If no matching services, exclude this expert
      if (!selectedBio || allMatchingServices.length === 0) return null

      // Remove duplicate services by reasonId (optional, if needed)
      const uniqueServices = Array.from(new Map(allMatchingServices.map((s) => [s.reasonId, s])).values())

      return {
        expertBio: selectedBio,
        services: uniqueServices
      }
    })
    .filter(Boolean) as CareTeamExpertWithTiles[]
}

/**
 * Enriches consolidated expert bios with tile display info fetched separately.
 * @param consolidatedExpertBios Consolidated expert bios from consolidateExpertBios.
 * @param tileDisplays Array of tile display data.
 * @returns Enriched expert bios with tileEntry added to each service where available.
 */
export const updateExpertBiosWithTileDisplays = (
  consolidatedExpertBios: CareTeamExpertWithTiles[],
  tileDisplays?: Array<{ id: string; tileDisplay?: TileDisplay }>
): CareTeamExpertWithTiles[] => {
  if (!tileDisplays) return consolidatedExpertBios

  // Build a lookup map: tileEntryId => TileDisplay
  const tileDisplaysById = Object.fromEntries(
    tileDisplays.filter((td) => td.tileDisplay).map((td) => [td.id, td.tileDisplay as TileDisplay])
  )

  return consolidatedExpertBios.map((expert) => ({
    ...expert,
    services: expert.services.map((service) => ({
      ...service,
      tileEntry: service.contentfulTileEntryId ? tileDisplaysById[service.contentfulTileEntryId] : undefined
    }))
  }))
}

/**
 * Determines whether the service selection step can be skipped for a given expert.
 * This is true when:
 * 1. The expert has exactly one service, OR
 * 2. There is only one displayable service (i.e., it has a tile and contentfulTileEntryId), OR
 * 3. None of the services have a contentfulTileEntryId (i.e., no displayable tiles).
 *
 * @param expert - The expert whose services are being evaluated.
 * @returns A boolean indicating whether the service selection step can be skipped.
 */
export function shouldSkipServiceSelection(expert?: CareTeamExpertWithTiles) {
  const servicesWithTile = expert?.services.filter((service) => service.tileEntry && service.contentfulTileEntryId)

  const isSkippable =
    expert?.services.length === 1 ||
    servicesWithTile.length === 1 ||
    expert?.services.every((service) => !service.contentfulTileEntryId)

  return isSkippable
}
